import React, { Component, HTMLAttributes, ReactElement } from 'react';
import styled from 'styled-components';

export enum ButtonColor {
  Yellow = '#ffbb14',
  Velvet = '#33d1ff',
  White = '#fff',
  Blue = '#33d1ff',
  Transparent = 'transparent',
  Red = '#ff3c78',
  LightVioletExalink = '#e8d1fc',
  DarkVioletExalink = '#7708ff'
}

export enum Justify {
  Left = 'flex-start',
  Right = 'flex-end',
  Center = 'center'
}

interface Props {
  textColor?: ButtonColor | string;
  hoverTextColor?: ButtonColor | string;
  backgroundColor?: ButtonColor | string;
  hoverColor?: ButtonColor | string;
  borderColor?: ButtonColor | string;
  minWidth?: string;
  outlined?: boolean;
  lowerCased?: boolean;
  stretched?: boolean;
  to?: string;
  invisible?: boolean;
  justify?: Justify;
  responsive?: boolean;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  id?: string;
  compact?: boolean;
  noContainer?: boolean;
  children?: ReactElement;
}

type ButtonProps = HTMLAttributes<HTMLButtonElement> & Props;
type LinkProps = HTMLAttributes<HTMLAnchorElement> & Props;

export class CustomizableButton extends Component<Props> {
  public render() {
    const {
      to,
      responsive = false,
      justify = Justify.Left,
      invisible = false,
      stretched = false,
      outlined = false,
      lowerCased = false,
      backgroundColor = ButtonColor.Blue,
      hoverColor = ButtonColor.White,
      textColor = ButtonColor.White,
      hoverTextColor = ButtonColor.Blue,
      disabled = false,
      minWidth,
      noContainer,
      ...otherProps
    } = this.props;

    const LinkOrButton = to ? (
      <StyledLink
        href={to}
        target="_blank"
        responsive={responsive}
        stretched={stretched}
        outlined={outlined}
        lowerCased={lowerCased}
        backgroundColor={backgroundColor}
        hoverColor={hoverColor}
        minWidth={minWidth}
        {...otherProps}
      />
    ) : (
      <StyledButton
        disabled={disabled}
        stretched={stretched}
        outlined={outlined}
        lowerCased={lowerCased}
        backgroundColor={backgroundColor}
        hoverColor={hoverColor}
        textColor={textColor}
        hoverTextColor={hoverTextColor}
        minWidth={minWidth}
        {...otherProps}
      />
    );

    return (
      <>
        {!noContainer ? (
          <Container justify={justify} invisible={invisible}>
            {LinkOrButton}
          </Container>
        ) : (
          LinkOrButton
        )}
      </>
    );
  }
}

const style = (props: Props) => `
  display: flex;
  font-size: 14px;
  font-weight: 400;
  min-height: ${props.compact ? '36px' : '56px'};
  padding: 0px 30px;
  text-transform: ${props.lowerCased ? 'unset' : 'uppercase'};
  text-decoration: none;
  text-align: center;
  justify-content: center;
  cursor: pointer;  
  width: ${props.stretched ? '100%' : 'auto'};
  min-width: ${props.minWidth ? props.minWidth : 'auto'};
  margin: ${props.compact ? '5px' : '20px 0'};
  padding: ${props.compact ? '5px' : '5px 10px'};
  align-items: center;

  color: ${props.textColor ? props.textColor : props.outlined ? props.backgroundColor : '#fff'};
  background-color: ${props.outlined ? '#fff' : props.backgroundColor};
  border: ${props.outlined ? `2px solid ${props.borderColor || props.backgroundColor}` : props.borderColor ? `2px solid ${props.borderColor}` : 'none'};

  &:hover {
    background-color: ${props.outlined ? props.backgroundColor : props.hoverColor};
    color: ${props.hoverTextColor ? props.hoverTextColor : props.outlined ? '#fff' : props.backgroundColor};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: ${props.outlined ? '#fff' : props.backgroundColor};
      color: ${props.outlined ? props.backgroundColor : '#fff'};
    }
  }

  @media (max-width: 1024px) {
    margin: 0px 20px 0px 0px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    height: 37px;
  }
 
  @media (max-width: 414px) {
    font-size: 12px;
    height: 25px;
  }
`;

const Container = styled.div<Props>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.justify};
  visibility: ${props => (props.invisible ? 'hidden' : 'visible')};
`;

const StyledLink = styled.a<LinkProps>`
  ${props => style(props)}
`;

const StyledButton = styled.button<ButtonProps>`
  ${props => style(props)}
`;
